/**
 * カラーオプションの型定義とユーティリティ
 *
 * 注意: 色マスター本体は Go 側 ( go/repository/data/colors.json ) を SSOT として管理しています。
 * フロントは API 経由で取得し、本ファイルは UI 側で必要な型 / カラートーンの表示定義のみを保持します。
 */

import { ColorTone } from '@ai-color-modules/gen_proto/api_proto/v1/color_pb'

/** メーカー名（APIから動的に取得。固定リストではなくstringで管理） */
export type Manufacturer = string

/**
 * カラートーン（色のグループ分け）。
 *
 * 「12 種のチップから選ぶ」という UI 上の実態に合わせ、proto の `ColorTone` enum を
 * そのまま型として使う（文字列 union を別途持つと proto と二重管理になるため）。
 * DB は VARCHAR で日本語ラベルを保持しており、enum ↔ ラベルの変換は Go 側
 * ( go/models/color_tone.go ) を SSOT とする。
 */
export { ColorTone }

/**
 * カラートーンの定義（表示ラベル・代表色・順序）。
 * 並びは proto の enum 定義順に合わせている。
 */
export const colorTones: { tone: ColorTone; label: string; color: string }[] = [
  { tone: ColorTone.WHITE, label: 'ホワイト系', color: '#FFFFFF' },
  { tone: ColorTone.GRAY, label: 'グレー系', color: '#EFEFEF' },
  { tone: ColorTone.BEIGE, label: 'ベージュ系', color: '#F3E9D7' },
  { tone: ColorTone.BROWN, label: 'ブラウン系', color: '#BC9F80' },
  { tone: ColorTone.BLACK, label: 'ブラック系', color: '#1D1D1D' },
  { tone: ColorTone.RED, label: 'レッド系', color: '#DC6C5E' },
  { tone: ColorTone.ORANGE, label: 'オレンジ系', color: '#DCA468' },
  { tone: ColorTone.YELLOW, label: 'イエロー系', color: '#F1D44D' },
  { tone: ColorTone.GREEN, label: 'グリーン系', color: '#87A960' },
  { tone: ColorTone.BLUE, label: 'ブルー系', color: '#5472B9' },
  { tone: ColorTone.PURPLE, label: 'パープル系', color: '#C090CA' },
  { tone: ColorTone.PINK, label: 'ピンク系', color: '#F3A6BA' },
]

const colorToneLabels = new Map(colorTones.map(({ tone, label }) => [tone, label]))

/**
 * カラートーンの日本語表示ラベルを返す。
 * UNSPECIFIED（トーン未設定の色 / 履歴色）は空文字を返す。
 */
export const getColorToneLabel = (tone: ColorTone): string => colorToneLabels.get(tone) ?? ''

export interface ColorOption {
  /**
   * 色マスター（colors テーブル）のサロゲートキー。
   *
   * React のリスト key はこの id を使うこと（`ColorPalette` 等）。色コード + hex の
   * 合成 key は本番データで一意にならない（例: ガイナが日塗工の色コードを同一 hex で
   * 再録しており 66 組衝突）。key 衝突は reconciliation を破壊し、フィルタ切替時に
   * 前状態の色ボタンが DOM に残存する実バグを起こした。
   *
   * DB 由来でない合成オプション（履歴色フォールバック）は負の id を採番する
   * （`buildColorOptionsFromParameters` 参照。正の DB id と衝突しない）。
   */
  id: bigint
  value: string
  label: string
  color: string
  manufacturer: Manufacturer
  /** カラートーン。トーン未設定の色や履歴色は UNSPECIFIED */
  tone: ColorTone
  r: number
  g: number
  b: number
}

/**
 * @deprecated 色マスターは API ( ListCompanyColors / ListSharedCompanyColors ) から取得してください。
 * このエクスポートは互換のために空配列を返します。
 */
export const colorOptions: ColorOption[] = []
